import { FieldSpan, LayoutSettings, TextScale } from '../types/highlights'

interface TextScaleStyle {
  label: string
  height: string
  labelText: string
  valueText: string
  padding: string
  flagText: string
}

export const textScaleStyles: Record<TextScale, TextScaleStyle> = {
  sm: {
    label: 'Small',
    height: 'h-[40px]',
    labelText: 'text-[9.5px]',
    valueText: 'text-[12px]',
    padding: 'pl-2 pr-1',
    flagText: 'text-[9.5px]',
  },
  md: {
    label: 'Medium',
    height: 'h-[46px]',
    labelText: 'text-[10.5px]',
    valueText: 'text-[13px]',
    padding: 'pl-2.5 pr-1.5',
    flagText: 'text-[10.5px]',
  },
  lg: {
    label: 'Large',
    height: 'h-[54px]',
    labelText: 'text-[11px]',
    valueText: 'text-[15px]',
    padding: 'pl-3 pr-2',
    flagText: 'text-[11px]',
  },
}

export const textScaleOrder: TextScale[] = ['sm', 'md', 'lg']

export const columnWidthOptions: { label: string; value: number }[] = [
  { label: 'Compact', value: 170 },
  { label: 'Standard', value: 210 },
  { label: 'Wide', value: 260 },
]

export const spanOptions: { label: string; value: FieldSpan }[] = [
  { label: 'Auto', value: 'auto' },
  { label: '¼×', value: 0.25 },
  { label: '½×', value: 0.5 },
  { label: '¾×', value: 0.75 },
  { label: '1×', value: 1 },
  { label: '2×', value: 2 },
  { label: '3×', value: 3 },
]

/** Each 1× column is split into this many grid tracks so fractional widths align. */
export const GRID_SUBDIVISIONS = 4

export const GRID_GAP_PX = 8

/** How many whole 1× columns fit in a container of the given width. */
export function unitsForWidth(containerWidth: number, columnWidth: number): number {
  return Math.max(1, Math.floor((containerWidth + GRID_GAP_PX) / (columnWidth + GRID_GAP_PX)))
}

/** Convert a width in 1× units to grid tracks, clamped to the row. */
export function toGridTracks(units: number, totalTracks: number): number {
  return Math.min(totalTracks, Math.max(1, Math.round(units * GRID_SUBDIVISIONS)))
}

export function formatSpan(units: number): string {
  if (units === 0.25) return '¼'
  if (units === 0.5) return '½'
  if (units === 0.75) return '¾'
  return String(units)
}

export const defaultLayout: LayoutSettings = {
  uniformSizing: false,
  columnWidth: 210,
  textScale: 'md',
}

/** Columns a field needs when its width is set to Auto, based on value length. */
export function autoSpan(characters: number): 1 | 2 | 3 {
  if (characters <= 20) return 1
  if (characters <= 36) return 2
  return 3
}

/** Width of a field in 1× units. */
export function resolveSpan(
  span: FieldSpan | undefined,
  valueLength: number,
  layout: LayoutSettings,
): number {
  if (layout.uniformSizing) return 1
  if (span === undefined || span === 'auto') return autoSpan(valueLength)
  return span
}

export function truncateValue(value: string, maxChars?: number): string {
  if (!maxChars || maxChars <= 0 || value.length <= maxChars) return value
  return `${value.slice(0, maxChars).trimEnd()}…`
}
